import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

/** Pinch-to-zoom stile Instagram sulle foto del feed: due dita per zoomare, al rilascio torna a posto. */
export const PinchZoomMedia = ({ children, className = "", style, onDoubleClick, testId }) => {
  const ref = useRef(null);
  const gRef = useRef(null);
  const [t, setT] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prevent = (e) => { if (gRef.current && e.touches.length > 1) e.preventDefault(); };
    el.addEventListener("touchmove", prevent, { passive: false });
    return () => el.removeEventListener("touchmove", prevent);
  }, []);

  const onTouchStart = (e) => {
    if (e.touches.length !== 2) return;
    const r = ref.current.getBoundingClientRect();
    const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left;
    const my = (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top;
    gRef.current = { d: dist(e.touches), mx, my };
  };

  const onTouchMove = (e) => {
    const g = gRef.current;
    if (!g || e.touches.length !== 2) return;
    const r = ref.current.getBoundingClientRect();
    const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left;
    const my = (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top;
    setT({
      scale: Math.min(3.5, Math.max(1, dist(e.touches) / g.d)),
      x: mx - g.mx, y: my - g.my,
      ox: g.mx, oy: g.my,
      done: false,
    });
  };

  const onTouchEnd = (e) => {
    if (e.touches.length >= 2) return;
    gRef.current = null;
    setT((cur) => (cur ? { ...cur, scale: 1, x: 0, y: 0, done: true } : null));
    setTimeout(() => setT((cur) => (cur?.done ? null : cur)), 320);
  };

  const zooming = !!t && (!t.done || t.scale > 1);
  const clipStyle = t
    ? {
        transform: `translate(${t.x}px, ${t.y}px) scale(${t.scale})`,
        transformOrigin: `${t.ox}px ${t.oy}px`,
        transition: t.done ? "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)" : "none",
        willChange: "transform",
      }
    : undefined;

  return (
    <>
      {t && createPortal(
        <div
          className="fixed inset-0 bg-black pointer-events-none"
          style={{
            zIndex: 80,
            opacity: t.done ? 0 : Math.min(0.75, (t.scale - 1) * 0.9),
            transition: t.done ? "opacity 0.3s ease" : "none",
          }}
          data-testid="pinch-zoom-backdrop"
        />,
        document.body
      )}
      <div
        ref={ref}
        className={className}
        style={{ ...style, position: "relative", zIndex: zooming ? 90 : undefined, touchAction: "pan-y" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        onDoubleClick={onDoubleClick}
        data-testid={testId}
      >
        <div className="relative w-full h-full overflow-hidden" style={clipStyle} data-testid="pinch-zoom-clip">
          {children}
        </div>
      </div>
    </>
  );
};

export default PinchZoomMedia;
